import { SubscriptionTier } from '../types/subscription';

const UPLOAD_LIMITS: Record<SubscriptionTier, number> = {
  free: 3,
  personal: 4,
  professional: 5
};

const STORAGE_KEY_PREFIX = 'exifapp_uploads_';
const ONE_HOUR_MS = 60 * 60 * 1000;

interface UploadRecord {
  timestamp: number;
}

/**
 * Get storage key for a user
 */
function getStorageKey(userId: string | null | undefined): string {
  if (!userId) {
    return `${STORAGE_KEY_PREFIX}anonymous`;
  }
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

/**
 * Get upload limit for a subscription tier
 */
export function getUploadLimit(tier: SubscriptionTier): number {
  return UPLOAD_LIMITS[tier];
}

/**
 * Get upload records from storage
 */
function getUploadRecords(userId: string | null | undefined): UploadRecord[] {
  try {
    const key = getStorageKey(userId);
    const stored = localStorage.getItem(key);
    if (!stored) return [];
    
    const records: UploadRecord[] = JSON.parse(stored);
    return records.filter(record => record && typeof record.timestamp === 'number');
  } catch (error) {
    console.error('Error reading upload records:', error);
    return [];
  }
}

/**
 * Save upload records to storage
 */
function saveUploadRecords(userId: string | null | undefined, records: UploadRecord[]): void {
  try {
    const key = getStorageKey(userId);
    localStorage.setItem(key, JSON.stringify(records));
  } catch (error) {
    console.error('Error saving upload records:', error);
  }
}

/**
 * Clean up old upload records (older than 1 hour)
 */
function cleanupOldRecords(records: UploadRecord[]): UploadRecord[] {
  const now = Date.now();
  const oneHourAgo = now - ONE_HOUR_MS;
  return records.filter(record => record.timestamp > oneHourAgo);
}

/**
 * Get upload count in the last hour
 */
export function getUploadCountInLastHour(userId: string | null | undefined): number {
  const records = getUploadRecords(userId);
  const recentRecords = cleanupOldRecords(records);
  
  // Update storage with cleaned records
  if (recentRecords.length !== records.length) {
    saveUploadRecords(userId, recentRecords);
  }
  
  return recentRecords.length;
}

/**
 * Record a new upload
 */
export function recordUpload(userId: string | null | undefined): void {
  const records = getUploadRecords(userId);
  const cleanedRecords = cleanupOldRecords(records);
  
  // Add new upload record
  cleanedRecords.push({ timestamp: Date.now() });
  
  // Save updated records
  saveUploadRecords(userId, cleanedRecords);
}

/**
 * Check if user can upload (hasn't reached limit)
 */
export function canUpload(
  userId: string | null | undefined,
  tier: SubscriptionTier
): boolean {
  const currentCount = getUploadCountInLastHour(userId);
  const limit = getUploadLimit(tier);
  return currentCount < limit;
}

/**
 * Get remaining uploads for the hour
 */
export function getRemainingUploads(
  userId: string | null | undefined,
  tier: SubscriptionTier
): number {
  const currentCount = getUploadCountInLastHour(userId);
  const limit = getUploadLimit(tier);
  const remaining = limit - currentCount;
  return Math.max(0, remaining);
}

/**
 * Get time until next upload slot is available (in milliseconds)
 */
export function getTimeUntilNextSlot(userId: string | null | undefined): number | null {
  const records = getUploadRecords(userId);
  const recentRecords = cleanupOldRecords(records);
  
  if (recentRecords.length === 0) {
    return null; // No uploads, can upload now
  }
  
  // Find the oldest upload in the last hour
  const oldestTimestamp = Math.min(...recentRecords.map(r => r.timestamp));
  const oneHourAfterOldest = oldestTimestamp + ONE_HOUR_MS;
  const now = Date.now();
  
  if (oneHourAfterOldest <= now) {
    return null; // Can upload now
  }
  
  return oneHourAfterOldest - now;
}

/**
 * Format time remaining until next upload slot
 */
export function formatTimeUntilNextSlot(ms: number | null): string {
  if (ms === null) return '';
  
  const minutes = Math.ceil(ms / (60 * 1000));
  if (minutes < 60) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return `${hours} hour${hours !== 1 ? 's' : ''}`;
  }
  return `${hours} hour${hours !== 1 ? 's' : ''} ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}`;
}

/**
 * Clear all upload records (useful for testing or reset)
 */
export function clearUploadRecords(userId: string | null | undefined): void {
  try {
    const key = getStorageKey(userId);
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error clearing upload records:', error);
  }
}
